import { Link } from 'react-router-dom';

/**
 * SCO-70 — third-party open-source attributions. Add an entry to `DEPENDENCIES` whenever a
 * new dependency with attribution requirements is introduced.
 */
interface License {
  scope: string;
  name: string;
  url: string;
}
interface Dependency {
  name: string;
  by: string;
  url: string;
  what: string;
  licenses: License[];
  note?: string;
}

const MIT = 'https://opensource.org/license/mit';
const APACHE2 = 'https://www.apache.org/licenses/LICENSE-2.0';

const DEPENDENCIES: Dependency[] = [
  {
    name: 'open-scout-api',
    by: 'sethmay',
    url: 'https://github.com/sethmay/open-scout-api',
    what: 'Cub Scout adventure catalog, requirement sets, and lifecycle data that power the catalog, meeting planning, and advancement tracking.',
    licenses: [
      { scope: 'Dataset', name: 'CC BY-NC-SA 4.0', url: 'https://creativecommons.org/licenses/by-nc-sa/4.0/' },
      { scope: 'Code', name: 'MIT', url: MIT },
    ],
    note: 'Requirement text within the dataset is © Scouting America, reproduced with attribution for non-commercial Scouting use, and is not covered by the dataset licence.',
  },
  {
    name: 'React',
    by: 'Meta and contributors',
    url: 'https://react.dev',
    what: 'Front-end UI library.',
    licenses: [{ scope: 'Code', name: 'MIT', url: MIT }],
  },
  {
    name: 'Vite',
    by: 'Evan You and Vite contributors',
    url: 'https://vitejs.dev',
    what: 'Front-end build tool and dev server.',
    licenses: [{ scope: 'Code', name: 'MIT', url: MIT }],
  },
  {
    name: 'Express',
    by: 'OpenJS Foundation and contributors',
    url: 'https://expressjs.com',
    what: 'HTTP server framework for the API.',
    licenses: [{ scope: 'Code', name: 'MIT', url: MIT }],
  },
  {
    name: 'Prisma',
    by: 'Prisma Data, Inc.',
    url: 'https://www.prisma.io',
    what: 'Database schema, migrations, and query client (SQLite).',
    licenses: [{ scope: 'Code', name: 'Apache-2.0', url: APACHE2 }],
  },
  {
    name: 'Zod',
    by: 'Colin McDonnell and contributors',
    url: 'https://zod.dev',
    what: 'Runtime schema validation for API requests and configuration.',
    licenses: [{ scope: 'Code', name: 'MIT', url: MIT }],
  },
];

export function Attributions() {
  return (
    <div className="legal-page">
      <p className="muted small">
        <Link to="/">← Back</Link> · <Link to="/about">About &amp; legal</Link>
      </p>
      <h1>Open-source attributions</h1>
      <p className="muted">
        DenLodge is built with the following third-party open-source projects. We’re grateful to
        their authors and contributors.
      </p>

      {DEPENDENCIES.map((d) => (
        <section key={d.name} className="attribution-entry">
          <h2>
            <a href={d.url} target="_blank" rel="noreferrer">
              {d.name}
            </a>
          </h2>
          <p className="muted small">by {d.by}</p>
          <p>{d.what}</p>
          <ul className="reqs">
            {d.licenses.map((l) => (
              <li key={l.scope}>
                {l.scope}:{' '}
                <a href={l.url} target="_blank" rel="noreferrer">
                  {l.name}
                </a>
              </li>
            ))}
          </ul>
          {d.note && <p className="muted small">{d.note}</p>}
        </section>
      ))}
    </div>
  );
}
